import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, FileSpreadsheet, Loader2, RefreshCw, Search, X } from 'lucide-react';
import type { LinhaDatasBoleto } from '../../../types';
import { carregarDatasBoletos } from '../../../src/services/datasFaturasApi';
import { dataBr, formatarDocumento, fracao, numerosUc, rotuloMes, type FiltroEmpresa } from '../../../src/lib/datasFaturas';
import {
  CAMPOS_DATA_BOLETO, coberturaBoletosPorMes, filtrarBoletos, leiturasFaltandoBoleto, resumirBoletos,
  rotuloStatus, statusOrdenados, type FiltroTipo,
} from '../../../src/lib/datasBoletos';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import AbasDatas from './AbasDatas';

// Relatório "Datas dos boletos": o par do "Datas das faturas" para o boleto que o VigorHub
// emite (Vigor pelo Sicoob, parceiras pelo Asaas) — leituras do ciclo que ele cobra,
// emissão, envio, vencimento e pagamento. Uma linha por boleto. Carrega tudo de uma vez
// (RPC) e filtra no navegador — ~7 mil linhas cabem fácil.

const POR_PAGINA = 50;

const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const int = (v: number) => v.toLocaleString('pt-BR');

const CINZA = 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-300';
const TOM_STATUS: Record<string, string> = {
  Pago: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  Quitado: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  Vencido: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300',
  Parcelado: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};
const tomStatus = (s: string) => TOM_STATUS[s] ?? CINZA;

const OPCOES_TIPO: { valor: FiltroTipo; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Todos os boletos' },
  { valor: 'mensais', rotulo: 'Só mensais (conta do mês)' },
  { valor: 'avulsos', rotulo: 'Só avulsos (acordo, parcela)' },
];

const LEITURAS = CAMPOS_DATA_BOLETO.filter((c) => c.leitura);
// Pagamento tem coluna própria: vazio, mostra o status do boleto no lugar.
const DATAS_BOLETO = CAMPOS_DATA_BOLETO.filter((c) => !c.leitura && c.chave !== 'pagamento');
const PAGAMENTO = CAMPOS_DATA_BOLETO.find((c) => c.chave === 'pagamento')!;

const DatasBoletosView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [linhas, setLinhas] = useState<LinhaDatasBoleto[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);

  const [busca, setBusca] = useState('');
  const [empresa, setEmpresa] = useState<FiltroEmpresa>('todas');
  const [tipo, setTipo] = useState<FiltroTipo>('todos');
  const [mesDe, setMesDe] = useState('');
  const [mesAte, setMesAte] = useState('');
  const [pagina, setPagina] = useState(0);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setLinhas(await carregarDatasBoletos());
    } catch (e: any) {
      setErro(e?.message || 'Falha ao carregar as datas dos boletos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Filtro mudou → volta para a 1ª página (ajustado no render, sem efeito extra).
  const chaveFiltro = `${busca}|${empresa}|${tipo}|${mesDe}|${mesAte}`;
  const [chaveAnterior, setChaveAnterior] = useState(chaveFiltro);
  if (chaveFiltro !== chaveAnterior) {
    setChaveAnterior(chaveFiltro);
    setPagina(0);
  }
  // Busca re-filtra ~7 mil linhas a cada tecla: adiada, o input não engasga.
  const buscaAdiada = useDeferredValue(busca);

  const todas = linhas ?? [];
  const temParceira = useMemo(() => todas.some((l) => l.empresa), [todas]);
  const meses = useMemo(() => [...new Set(todas.map((l) => l.mes_iso))].sort(), [todas]);

  // A faixa de cobertura ignora o período (é ela que escolhe o período), mas respeita
  // os outros filtros — assim ela mostra onde estão os buracos do recorte atual.
  const semPeriodo = useMemo(
    () => filtrarBoletos(todas, { busca: buscaAdiada, empresa, tipo }),
    [todas, buscaAdiada, empresa, tipo],
  );
  const filtradas = useMemo(
    () => filtrarBoletos(semPeriodo, { mesDe: mesDe || undefined, mesAte: mesAte || undefined }),
    [semPeriodo, mesDe, mesAte],
  );
  const cobertura = useMemo(() => coberturaBoletosPorMes(semPeriodo), [semPeriodo]);
  const maiorMes = useMemo(() => Math.max(1, ...cobertura.map((c) => c.total)), [cobertura]);
  const resumo = useMemo(() => resumirBoletos(filtradas), [filtradas]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const visiveis = filtradas.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA);

  const mesSelecionado = mesDe && mesDe === mesAte ? mesDe : '';
  const alternarMes = (m: string) => {
    if (mesSelecionado === m) { setMesDe(''); setMesAte(''); }
    else { setMesDe(m); setMesAte(m); }
  };
  const temFiltro = !!(busca || empresa !== 'todas' || tipo !== 'todos' || mesDe || mesAte);
  const limpar = () => { setBusca(''); setEmpresa('todas'); setTipo('todos'); setMesDe(''); setMesAte(''); };

  const recorte = useMemo(() => {
    const partes: string[] = [];
    if (mesDe || mesAte) {
      partes.push(mesDe === mesAte ? `Mês ${rotuloMes(mesDe)}` : `Período ${mesDe ? rotuloMes(mesDe) : 'início'} a ${mesAte ? rotuloMes(mesAte) : 'hoje'}`);
    }
    if (empresa !== 'todas') partes.push(empresa === 'vigor' ? 'Só Vigor' : 'Só empresas parceiras');
    if (tipo !== 'todos') partes.push(OPCOES_TIPO.find((o) => o.valor === tipo)!.rotulo);
    if (buscaAdiada.trim()) partes.push(`Busca "${buscaAdiada.trim()}"`);
    return partes.length ? partes.join(' · ') : 'Todos os boletos';
  }, [mesDe, mesAte, empresa, tipo, buscaAdiada]);

  const exportar = async () => {
    if (!filtradas.length) return;
    setExportando(true);
    try {
      const { exportarDatasBoletos } = await import('./exportarDatasBoletos');
      await exportarDatasBoletos({ linhas: filtradas, recorte, geradoPor: profile?.full_name || 'VigorHub' });
    } catch (e: any) {
      addToast(e?.message || 'Falha ao gerar a planilha.', 'error');
    } finally {
      setExportando(false);
    }
  };

  const seletor = 'px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary/40 outline-none';
  const celData = 'px-1.5 py-2.5 text-center font-mono text-[11.5px] tabular-nums whitespace-nowrap';
  const vazio = (titulo: string) => <span className="text-gray-300 dark:text-gray-600" title={titulo}>—</span>;

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80 dark:text-primary-light">Gestão · Boletos da Vigor</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-poppins mt-1">Datas dos boletos</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm max-w-2xl">
            Leituras, emissão, envio, vencimento e pagamento de cada boleto emitido pelo sistema. Uma linha por boleto.
          </p>
          <div className="mt-3"><AbasDatas atual="boletos" /></div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={carregar}
            disabled={carregando}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium whitespace-nowrap rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} /> Atualizar
          </button>
          <button
            onClick={exportar}
            disabled={exportando || !filtradas.length}
            title={filtradas.length ? `Baixar ${int(filtradas.length)} boletos em Excel` : 'Nada para exportar'}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-xl bg-primary text-white shadow-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {exportando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Exportar Excel
          </button>
        </div>
      </div>

      {erro && (
        <div className="rounded-2xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
          <span>{erro}</span>
          <button onClick={carregar} className="font-semibold underline underline-offset-2">Tentar de novo</button>
        </div>
      )}

      {/* Indicadores do recorte */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { rotulo: 'Boletos', valor: int(resumo.boletos), nota: `${int(resumo.meses)} ${resumo.meses === 1 ? 'mês' : 'meses'}` },
          { rotulo: 'UCs', valor: int(resumo.ucs), nota: temParceira ? 'Vigor e parceiras' : 'Vigor' },
          { rotulo: 'Com emissão', valor: pct(fracao(resumo.comEmissao, resumo.boletos)), nota: `${int(resumo.comEmissao)} boletos`, barra: fracao(resumo.comEmissao, resumo.boletos) },
          { rotulo: 'Com as 3 leituras', valor: pct(fracao(resumo.leiturasCompletas, resumo.mensais)), nota: `dos ${int(resumo.mensais)} boletos mensais (avulso não tem leitura)`, barra: fracao(resumo.leiturasCompletas, resumo.mensais) },
        ].map((k) => (
          <div key={k.rotulo} className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{k.rotulo}</p>
            <p className="text-2xl font-bold font-poppins text-gray-900 dark:text-white tabular-nums mt-1">
              {carregando && !linhas ? <span className="inline-block w-16 h-7 rounded bg-gray-100 dark:bg-gray-700 animate-pulse" /> : k.valor}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate" title={k.nota}>{k.nota}</p>
            {k.barra !== undefined && (
              <div className="absolute left-0 bottom-0 h-1 w-full bg-gray-100 dark:bg-gray-700">
                <div className="h-full bg-primary transition-all duration-700" style={{ width: `${k.barra * 100}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Cobertura por mês: barra = boletos do mês; parte cheia = com emissão;
          marca âmbar = mensais com as 3 leituras (mês só de avulsos não tem marca). Clicar filtra o mês. */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary" /> Cobertura por mês
          </h2>
          <div className="flex items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary" /> com emissão</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary/20 dark:bg-primary-light/20" /> sem emissão</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-secondary" /> mensais com as 3 leituras</span>
          </div>
        </div>
        {carregando && !linhas ? (
          <div className="h-32 rounded-xl bg-gray-50 dark:bg-gray-900/40 animate-pulse" />
        ) : cobertura.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">Nenhum boleto no recorte.</p>
        ) : (
          <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
            {cobertura.map((c) => {
              const altura = 24 + (c.total / maiorMes) * 88;
              const ativo = mesSelecionado === c.mesIso;
              const foraDoPeriodo = (mesDe && c.mesIso < mesDe) || (mesAte && c.mesIso > mesAte);
              const pEmissao = fracao(c.comEmissao, c.total);
              const pLeit = fracao(c.leiturasCompletas, c.mensais);
              return (
                <button
                  key={c.mesIso}
                  onClick={() => alternarMes(c.mesIso)}
                  title={`${c.mes}: ${int(c.total)} boletos · ${pct(pEmissao)} com emissão${c.mensais ? ` · ${pct(pLeit)} dos mensais com as 3 leituras` : ' · só avulsos'}`}
                  className={`group flex-1 min-w-[44px] flex flex-col items-center gap-1.5 transition-opacity ${foraDoPeriodo ? 'opacity-35' : ''}`}
                >
                  <span className="text-[10px] tabular-nums text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200">{int(c.total)}</span>
                  <div
                    className={`relative w-full rounded-md overflow-hidden bg-primary/15 dark:bg-primary-light/15 ring-offset-2 dark:ring-offset-gray-800 transition-all ${ativo ? 'ring-2 ring-primary' : 'group-hover:ring-1 group-hover:ring-primary/40'}`}
                    style={{ height: altura }}
                  >
                    <div className="absolute inset-x-0 bottom-0 bg-primary transition-all duration-700" style={{ height: `${pEmissao * 100}%` }} />
                    {c.mensais > 0 && (
                      <div className="absolute inset-x-0 h-0.5 bg-secondary shadow-[0_0_0_1px_rgba(255,255,255,0.6)]" style={{ bottom: `calc(${pLeit * 100}% - 1px)` }} />
                    )}
                  </div>
                  <span className={`text-[10px] font-semibold tracking-wide whitespace-nowrap ${ativo ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`}>
                    {c.mes.slice(0, 3)}<span className="font-normal text-gray-400">/{c.mes.slice(-2)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 flex flex-col xl:flex-row gap-2.5">
        {/* self-start: quando os filtros quebram em duas linhas, a caixa não estica e a lupa
            fica no meio do campo, não no meio da faixa inteira. */}
        <div className="relative flex-1 min-w-[220px] self-start w-full xl:w-auto">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="UC, cliente ou CPF/CNPJ"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary/40 outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <select value={mesDe} onChange={(e) => setMesDe(e.target.value)} className={seletor} aria-label="Mês inicial">
              <option value="">Desde o início</option>
              {meses.map((m) => <option key={m} value={m}>{rotuloMes(m)}</option>)}
            </select>
            <span>até</span>
            <select value={mesAte} onChange={(e) => setMesAte(e.target.value)} className={seletor} aria-label="Mês final">
              <option value="">Hoje</option>
              {meses.map((m) => <option key={m} value={m}>{rotuloMes(m)}</option>)}
            </select>
          </div>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as FiltroTipo)} className={seletor} aria-label="Tipo de boleto">
            {OPCOES_TIPO.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
          </select>
          {temParceira && (
            <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 p-0.5">
              {(['todas', 'vigor', 'parceiras'] as FiltroEmpresa[]).map((e) => (
                <button
                  key={e}
                  onClick={() => setEmpresa(e)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-[10px] transition-colors ${empresa === e ? 'bg-primary text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100'}`}
                >
                  {e === 'todas' ? 'Todas' : e === 'vigor' ? 'Vigor' : 'Parceiras'}
                </button>
              ))}
            </div>
          )}
          {temFiltro && (
            <button onClick={limpar} className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-medium rounded-xl text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <X className="w-3.5 h-3.5" /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            {/* Dois andares, como na tabela das faturas: "Leitura" e "Boleto" sobre as datas. */}
            <thead className="bg-gray-50/80 dark:bg-gray-900/40 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th rowSpan={2} className="text-left font-semibold px-3 py-2 align-bottom">UC · Cliente</th>
                <th rowSpan={2} className="text-left font-semibold px-2 py-2 align-bottom">Mês</th>
                <th colSpan={LEITURAS.length} className="text-center font-semibold px-1.5 pt-2 pb-0.5 border-b border-gray-200/70 dark:border-gray-700">Leitura</th>
                <th colSpan={DATAS_BOLETO.length} className="text-center font-semibold px-1.5 pt-2 pb-0.5 border-b border-gray-200/70 dark:border-gray-700">Boleto</th>
                <th rowSpan={2} className="text-center font-semibold px-1.5 py-2 align-bottom" title={PAGAMENTO.explicacao}>Pagamento</th>
              </tr>
              <tr>
                {['Anterior', 'Atual', 'Próxima'].map((r) => (
                  <th key={r} className="text-center font-semibold px-1.5 pt-1 pb-2">{r}</th>
                ))}
                {DATAS_BOLETO.map((c) => (
                  <th key={c.chave} title={c.explicacao} className={`text-center font-semibold px-1.5 pt-1 pb-2 ${c.chave === 'emissao' ? 'text-primary dark:text-primary-light' : ''}`}>
                    {c.chave === 'envio' ? 'Envio' : c.rotulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
              {carregando && !linhas ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}><td colSpan={9} className="px-4 py-3"><div className="h-5 rounded bg-gray-50 dark:bg-gray-700/40 animate-pulse" /></td></tr>
                ))
              ) : visiveis.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">Nenhum boleto encontrado com esses filtros.</td></tr>
              ) : visiveis.map((l) => {
                const faltando = leiturasFaltandoBoleto(l);
                const { nova, antiga } = numerosUc(l);
                const status = rotuloStatus(l);
                return (
                  <tr key={l.fatura_id} className="hover:bg-gray-50/70 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2.5 max-w-[230px]">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="font-mono text-[12px] text-gray-800 dark:text-gray-100">{nova || antiga}</span>
                        {nova && antiga ? (
                          <span className="font-mono text-[10.5px] text-gray-400 dark:text-gray-500" title="Número antigo da mesma UC">{antiga}</span>
                        ) : null}
                        {l.empresa && <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-secondary/15 text-amber-700 dark:text-secondary">{l.empresa}</span>}
                      </div>
                      <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate" title={`${l.cliente || ''}${l.cpf_cnpj ? ` · ${formatarDocumento(l.cpf_cnpj)}` : ''}`}>
                        {l.cliente || <span className="italic">sem nome</span>}
                        {l.cpf_cnpj && <span className="text-gray-400 dark:text-gray-500"> · {formatarDocumento(l.cpf_cnpj)}</span>}
                      </p>
                    </td>
                    <td className="px-2 py-2.5 whitespace-nowrap">
                      <span className="font-semibold text-[12px] text-gray-700 dark:text-gray-200">{l.mes}</span>
                      {faltando.length > 0 && (
                        <span className="ml-1.5 text-[10px] text-amber-600 dark:text-amber-400" title={`Faltando: ${faltando.join(', ')}`}>●</span>
                      )}
                    </td>
                    {l.avulso ? (
                      <td colSpan={LEITURAS.length} className="px-1.5 py-2.5 text-center">
                        <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${CINZA}`} title="Boleto avulso (acordo, parcela, multa): não cobra a conta de energia de um mês, então não tem leitura.">
                          Avulso · sem leitura
                        </span>
                      </td>
                    ) : LEITURAS.map((c) => (
                      <td key={c.chave} className={`${celData} text-gray-600 dark:text-gray-300`}>
                        {l[c.chave] ? dataBr(l[c.chave]) : vazio(`${c.rotulo} não está na fatura nem no PDF da Equatorial`)}
                      </td>
                    ))}
                    {DATAS_BOLETO.map((c) => (
                      <td key={c.chave} className={`${celData} ${c.chave === 'emissao' ? 'font-semibold text-primary dark:text-primary-light' : 'text-gray-600 dark:text-gray-300'}`}>
                        {l[c.chave] ? dataBr(l[c.chave]) : vazio(c.explicacao)}
                      </td>
                    ))}
                    <td className={`${celData} text-gray-700 dark:text-gray-200`}>
                      {l.pagamento ? dataBr(l.pagamento) : (
                        <span className={`inline-block font-sans text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${tomStatus(status)}`} title="Sem data de pagamento: status do boleto">
                          {status}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtradas.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            <span className="tabular-nums">
              {int(pagina * POR_PAGINA + 1)}–{int(Math.min((pagina + 1) * POR_PAGINA, filtradas.length))} de {int(filtradas.length)}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPagina((p) => Math.max(0, p - 1))} disabled={pagina === 0} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30" aria-label="Página anterior"><ChevronLeft className="w-4 h-4" /></button>
              <span className="tabular-nums px-1">{pagina + 1} / {totalPaginas}</span>
              <button onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))} disabled={pagina >= totalPaginas - 1} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30" aria-label="Próxima página"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Status e de onde vem cada data */}
      {linhas && resumo.boletos > 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Status dos boletos</p>
          <div className="flex flex-wrap gap-2">
            {statusOrdenados(resumo.porStatus).map((s) => (
              <span key={s} className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg ${tomStatus(s)}`}>
                <strong className="font-semibold">{s}</strong>
                <span className="tabular-nums">{int(resumo.porStatus[s])}</span>
              </span>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
            Leituras: as gravadas na fatura, que são as que o boleto imprime; quando ela não tem (quase todos até MAR/2026), vêm do PDF da conta da Equatorial do mesmo mês.
            Emissão: registro do boleto no banco (Sicoob ou Asaas). Envio ao cliente: registrado pelo sistema a partir de MAR/2026.
          </p>
        </div>
      )}
    </div>
  );
};

export default DatasBoletosView;
